import type { ReactNode } from 'react';

import { useScrollNavigation } from '@/hook/useScrollNavigation';
import { VFlex, VListItem, VStack, cn } from '@/package';

const MouseKeyboardGuidePageContent = () => {
  const itemList = [
    { id: '1', name: 'How to use the mouse' },
    { id: '2', name: 'Basic shortcut guide' },
    { id: '3', name: 'How to customize hotkeys' },
    { id: '4', name: 'Import/Export shortcuts' },
  ];

  const { selectedItemId, ref, handleItemClick } = useScrollNavigation({
    itemList,
    initialSelectedId: '1',
  });

  return (
    <VFlex
      className="h-full w-full overflow-y-auto py-6"
      gap={1}
      justify="center"
      ref={ref}
    >
      <div className="sticky top-0 h-full w-[324px] min-w-[324px] rounded-md border border-skyBlue-200">
        <VListItem
          list={itemList}
          key={selectedItemId}
          value={
            itemList.find(item => item.id === selectedItemId) || itemList[0]
          }
          onItemClick={handleItemClick}
        />
      </div>

      <VStack className="h-full max-w-[864px] flex-1 pb-[100px]" gap={1.5}>
        <div data-section-id="1">
          <MouseGuide />
        </div>
        <div data-section-id="2">
          <ShortcutGuide />
        </div>
        <div data-section-id="3">
          <LabelingToolShortCut />
        </div>
        <div data-section-id="4">
          <ImportExportShortCut />
        </div>
      </VStack>
    </VFlex>
  );
};

const Title = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-headline-md font-bold">{children}</div>;
};

const MouseGuide = () => {
  return (
    <VStack className="w-full" gap={0.5}>
      <Title>How to use the mouse</Title>
      <div className="text-body-lg">
        The following table lists the mouse operations available in the VueX
        Labeling Tool.
      </div>

      <div className="w-full divide-y divide-gray-300 rounded-md border border-gray-300">
        <MouseGuideTableRow
          cellList={['Control Method', 'Description']}
          isHeader
        />
        <MouseGuideTableRow
          cellList={[
            'Left Click',
            <>
              • Object Selection: Click on a box to open the edit panel on the
              right, allowing bounding box editing in top/side/front views
              <br />
              • Object Box Creation: In 'Add Object Box' mode, click on empty
              areas to create object boxes
              <br />• Deselection: When an object is selected, click on empty
              space to deselect it
            </>,
          ]}
        />
        <MouseGuideTableRow
          cellList={[
            'Right Click',
            <>
              • Automatically selects created object boxes, starting from the
              first number
              <br />• When multiple boxes overlap, continue clicking to select
              them sequentially by number order
            </>,
          ]}
        />
        <MouseGuideTableRow
          cellList={[
            'Double Click',
            'Double-clicking after selecting an object displays object manipulation tools, allowing movement in all directions',
          ]}
        />
        <MouseGuideTableRow
          cellList={[
            'Left Drag',
            'Workspace Pan — Move the view in all directions',
          ]}
        />
        <MouseGuideTableRow
          cellList={[
            'Right Drag',
            'Workspace Rotation — Rotate the view at various angles',
          ]}
        />
        <MouseGuideTableRow
          cellList={[
            'Mouse Wheel',
            'Zoom In/Out — Zoom in or out on the workspace',
          ]}
        />
        <MouseGuideTableRow
          cellList={[
            'Ctrl + Left Drag',
            'Multi-selection — Hold Ctrl and drag in top view to select multiple objects',
          ]}
        />
      </div>
    </VStack>
  );
};

const MouseGuideTableRow = ({
  cellList,
  isHeader = false,
}: {
  cellList: ReactNode[];
  isHeader?: boolean;
}) => {
  return (
    <VFlex
      className={`divide-x divide-gray-300 text-body-md ${isHeader ? 'rounded-t-md bg-gray-200 font-bold text-gray-400' : ''}`}
    >
      <div className="w-[112px] p-2">{cellList[0]}</div>
      <div className="flex-1 p-2">{cellList[1]}</div>
    </VFlex>
  );
};

export const ShortcutGuideTable = () => {
  return (
    <div className="w-full divide-y divide-gray-300 rounded-md border border-gray-300">
      <ShortcutTableRow
        cellList={['Category', 'Function', 'Shortcut', 'Action']}
        isHeader
      />
      <ShortcutTableRow
        cellList={[
          'AI Execution',
          'AI-annotation',
          'Shift + A',
          'Execute AI-annotation for the current frame',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'AI Execution',
          'AI-assist',
          'Shift + S',
          'Execute AI-assist for the selected area',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Start Task',
          'Enter',
          'Enter "Add Object Box" mode',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Add Object Box Mode',
          'N',
          'Enter "Add Object Box" mode',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Create Object Box',
          'None',
          'In "Add Object Box" mode, click on empty area to create object box',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Object Class Switch',
          <div>
            • 1 : Car
            <br />• 2 : Truck
            <br />• 3 : Pedestrian
            <br />• 4 : Cyclist
            <br />• 5 : Van
            <br />• 6 : Bus
          </div>,
          'In "Add Object Box" mode, press number (n) to select corresponding class and click to create box',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Selection Mode',
          'None',
          'Enter selection mode or automatically switch to selection mode when clicking a box',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Select All',
          'Ctrl + A',
          'Select all objects',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Copy',
          'Ctrl + C',
          'Copy object after double-clicking with Ctrl + C',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Paste',
          'Ctrl + V',
          'Paste copied object to current position',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Rotate 90 degrees',
          'Tab',
          'Rotate selected box Orientation Angle by 90 degrees',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Rotate 180 degrees',
          'Q',
          'Rotate selected box Orientation Angle by 180 degrees',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Rotate left 10 degrees',
          'R',
          'In right auxiliary viewport panel (TOP), rotate selected box Orientation Angle 10 degrees to the left',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Rotate right 10 degrees',
          'F',
          'In right auxiliary viewport panel (TOP), rotate selected box Orientation Angle 10 degrees to the right',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Move up',
          'W',
          'In right auxiliary viewport panel (SIDE), move selected box upward',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Move down',
          'S',
          'In right auxiliary viewport panel (SIDE), move selected box downward',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Move left',
          'A',
          'In right auxiliary viewport panel (SIDE), move selected box to the left',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Move right',
          'D',
          'In right auxiliary viewport panel (SIDE), move selected box to the right',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Adjust box height',
          'None',
          'In right auxiliary viewport panel (SIDE), increase/decrease height of selected box',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Adjust box width',
          'None',
          'In right auxiliary viewport panel (SIDE), increase/decrease width of selected box',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Move up',
          'None',
          'In right auxiliary viewport panel (Front), move selected box upward',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Move down',
          'None',
          'In right auxiliary viewport panel (Front), move selected box downward',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Move left',
          'None',
          'In right auxiliary viewport panel (Front), move selected box to the left',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Editing',
          'Move right',
          'None',
          'In right auxiliary viewport panel (Front), move selected box to the right',
        ]}
      />
      <ShortcutTableRow
        cellList={['Object Editing', 'Delete', 'X', 'Delete selected object']}
      />
      <ShortcutTableRow
        cellList={[
          'Object Manipulation Tool',
          'Show resize UI',
          'Ctrl + Q',
          'While object manipulation tool is active, hold Ctrl + Q to display resize UI for adjustment',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Object Manipulation Tool',
          'Show rotation UI',
          'Ctrl + W',
          'While object manipulation tool is active, hold Ctrl + W to display rotation UI for adjustment',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Task Control',
          'Undo',
          'Ctrl + Z',
          'Cancel previous action',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Task Control',
          'Redo',
          'Ctrl + Y',
          'Cancel previous action',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Task Control',
          'Reset',
          'None',
          'Return current work in progress to initial state',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Toolbar Features',
          'Review',
          'None',
          'Review panel automatically expands on the left',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Toolbar Features',
          'View Switch',
          'V',
          'Each press of V key switches view in order (Perspective → Top → Front → Left)',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Toolbar Features',
          'Image View',
          'None',
          'When Image data is available, the panel automatically expands at the bottom left',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Toolbar Features',
          'Full Screen',
          'None',
          'Hide panels/toolbar and switch to full screen viewport only (can be disabled with Esc key)',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Toolbar Features',
          'Settings',
          'None',
          'Panel where you can configure PCD, bounding boxes, cameras, and other items',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Settings',
          'Grid',
          'G',
          'Press G key or use left toolbar > Settings > Grid On/Off toggle (Default: Show)',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Settings',
          'Driving Direction Arrow',
          'U',
          'Press U key or use left toolbar > Settings > Driving Direction Arrow On/Off toggle (Default: Show)',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Frame Control',
          'Frame Navigation',
          '← →',
          'Previous/Next frame navigation',
        ]}
      />
      <ShortcutTableRow
        cellList={[
          'Frame Control',
          'Frame Play/Pause',
          'spacebar',
          'Frame playback and pause',
        ]}
      />
    </div>
  );
};

const ShortcutGuide = () => {
  return (
    <VStack className="w-full" gap={0.5}>
      <Title>Basic shortcut guide</Title>
      <div className="text-body-lg">
        The following table lists the basic keyboard shortcuts. Shortcuts can be
        customized freely according to user preferences, and settings can be
        exported and imported as files. Some shortcuts only work in specific
        modes.
      </div>
      <ShortcutGuideTable />
    </VStack>
  );
};

const ShortcutTableRow = ({
  cellList,
  isHeader = false,
}: {
  cellList: ReactNode[];
  isHeader?: boolean;
}) => {
  return (
    <VFlex
      className={`divide-x divide-gray-300 text-body-md ${isHeader ? 'rounded-t-md bg-gray-200 font-semibold text-gray-400' : ''}`}
    >
      <div className="w-[164px] p-2">{cellList[0]}</div>
      <div className="w-[164px] p-2">{cellList[1]}</div>
      <div
        className={cn(
          'w-[164px] p-2',
          cellList[2] === 'None' && 'text-gray-400',
        )}
      >
        {cellList[2]}
      </div>
      <div className="flex-1 p-2">{cellList[3]}</div>
    </VFlex>
  );
};

const LabelingToolShortCut = () => {
  return (
    <VStack className="w-full" gap={0.5}>
      <Title>How to customize hotkeys</Title>
      <div className="text-body-lg">
        Hotkey customization can be set directly within the labeling tool, and
        you can even assign multiple hotkeys to a single feature.
      </div>
      <VStack>
        <div className="text-body-lg">
          1. Click ‘Preferences’ at the bottom of the toolbar on the left side
          of the labeling tool.
        </div>
        <div className="text-body-lg">
          2. At the bottom of the Settings panel, click ‘Customize shortcuts’.
        </div>
      </VStack>

      <img
        className="px-4"
        src="https://d1c9e0tayzp6id.cloudfront.net/labeling_tool_shortcut_guide_1.png"
      />

      <VStack>
        <div className="text-body-lg">
          3. In the Customize shortcuts modal that appears, you can view and
          edit the shortcuts for each feature.
        </div>
        <VFlex className="px-4 text-body-lg">
          <span>•</span> The ‘Add Shortcut’ button allows you to associate
          multiple shortcuts to a single function, and existing shortcuts can be
          deleted or edited.
        </VFlex>
      </VStack>

      <img
        className="px-4"
        src="https://d1c9e0tayzp6id.cloudfront.net/labeling_tool_shortcut_guide_2.png"
      />
    </VStack>
  );
};

const ImportExportShortCut = () => {
  return (
    <VStack className="w-full" gap={0.5}>
      <Title>Import/Export shortcuts</Title>
      <div className="text-body-lg">
        • Shortcut settings can be exported and imported in .json file format.
      </div>
      <div className="text-body-lg">
        • You can share your settings with other projects or users via a saved
        shortcut file.
      </div>
    </VStack>
  );
};

export default MouseKeyboardGuidePageContent;
